import Container from '@v2/shared/components/Container';
import Skeleton from '@v2/shared/components/Skeleton';
import { cn } from '@v2/shared/lib/utils';
import { SALE_CONTAINER } from '@v2/shared/lib/megaSale';

import ProductSkeleton from './ProductSkeleton';
import { CATEGORY_HEADING, PRODUCT_GRID } from './CategorySection';

/**
 * How many placeholder tiles to draw.
 *
 * Two rows of the widest grid, which is a screenful on a desktop and rather more than one
 * on a phone. The number is deliberately not "as many as the sale has": the sale is what
 * has not arrived yet.
 */
const TILE_COUNT = 6;

/**
 * The pills, as the widths of the words that are coming.
 *
 * A row of identical pills reads as a progress bar rather than as a navbar, so these vary
 * the way section names do. Widths rather than a count alone, because a sale's sections
 * are "Food & Drink" and "Home", not six of the same thing.
 */
const NAV_PILL_WIDTHS = ['w-24', 'w-32', 'w-20', 'w-28', 'w-36', 'w-24'];

/**
 * The sale, before it has arrived.
 *
 * This replaces a spinner over the words "Loading sale". A spinner says only that
 * something is happening; the shape of the page says what is coming, and it says it in
 * the place each piece will actually appear — so the banner, the navbar and the first
 * tiles do not shove each other around when the JSON lands.
 *
 * Everything here borrows its shape from the thing it stands in for: the banner's box
 * from the page's container, the pills from the navbar's own row, the heading from
 * `CATEGORY_HEADING`, the tiles from `ProductSkeleton`, which is already the tile's exact
 * size at every breakpoint. Nothing is stated twice, so nothing can drift.
 *
 * The whole thing is `aria-hidden` and the announcement is one sentence for a screen
 * reader, which is what the spinner's `role="status"` was carrying.
 */
export default function SaleSkeleton() {
  return (
    <>
      <p className="sr-only m-0" role="status" aria-live="polite">
        Loading sale
      </p>

      <div aria-hidden>
        {/*
          The banner, boxed exactly as SaleBanner boxes the real artwork, and about as
          tall: these files are still cut at the legacy page's 994x141, so 36 lands
          within a few pixels of what replaces it. A phone gets the mobile upload, which
          is a squarer crop, so it is a little shorter rather than a lot.
        */}
        <Container className={cn('pt-4', SALE_CONTAINER)}>
          <Skeleton className="h-28 w-full sm:h-32 md:h-36" />
        </Container>

        {/* The category navbar. Desktop only, because that is where the real one draws. */}
        <nav className="hidden md:block bg-white border-b-[1.5px] border-slate-200 shadow-sm">
          <Container>
            <ul className="list-none flex flex-wrap justify-center items-center gap-x-2 gap-y-1 m-0 py-2 px-0">
              {NAV_PILL_WIDTHS.map((width) => (
                <li key={width}>
                  <Skeleton className={cn('h-8 rounded-full', width)} />
                </li>
              ))}
            </ul>
          </Container>
        </nav>

        <Container className={cn('pt-5 pb-10', SALE_CONTAINER)}>
          {/* The bar is the height of the heading's own line, so the grid does not move. */}
          <div className={CATEGORY_HEADING}>
            <Skeleton className="h-5 w-5" />
            <Skeleton className="h-7 w-40" />
          </div>

          <div className={PRODUCT_GRID}>
            {Array.from({ length: TILE_COUNT }, (_, index) => (
              <ProductSkeleton key={index} shimmer />
            ))}
          </div>
        </Container>
      </div>
    </>
  );
}
